import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

const DEFAULT_TIME = 2000;
const DEFAULT_THROTTLE_TIME = 350;

/**
 * Хук для скрытия элементов если мышка не двигается.
 * Если вышли за пределы контейнера, то скрытие перестаёт работать, пока не вернёмся.
 * @param ref Ссылка на контейнер, к которому привязываем события наведения.
 * @param time Сколько ms будут показаны элементы до скрытия.
 * @param throttleTime Сколько ms будет задержка при срабатывании события move.
 * @returns
 */
export const useShowOnMovingPointer = ({
    ref,
    time = DEFAULT_TIME,
    throttleTime = DEFAULT_THROTTLE_TIME,
}: {
    ref: RefObject<HTMLDivElement | null>;
    time?: number;
    throttleTime?: number;
}) => {
    const [isInterfaceShown, setIsInterfaceShown] = useState(false);
    const timerRef = useRef<number | undefined>(undefined);
    const alwaysVisibleElementsRefsRef = useRef<Set<RefObject<HTMLElement | null>>>(new Set());

    // Функция для регистрации ref элементов, на которых интерфейс всегда должен быть виден
    const registerAlwaysVisibleElement = useCallback((elementRef: RefObject<HTMLElement | null>) => {
        alwaysVisibleElementsRefsRef.current.add(elementRef);
        return () => {
            alwaysVisibleElementsRefsRef.current.delete(elementRef);
        };
    }, []);

    // Утилита для проверки, находится ли элемент внутри одного из alwaysVisible элементов
    const isTargetInAlwaysVisible = useCallback((target: HTMLElement | null): boolean => {
        if (!target) return false;

        for (const elementRef of alwaysVisibleElementsRefsRef.current) {
            const element = elementRef.current;
            if (!element) continue;

            // Проверяем, находится ли целевой элемент внутри элемента или является им самим
            // Также проверяем родительские элементы, т.к. поповер может быть в портале
            let current: HTMLElement | null = target;
            while (current) {
                if (element.contains(current) || element === current) {
                    return true;
                }
                current = current.parentElement;
            }
        }
        return false;
    }, []);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;

        const showInterface = () => {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
            setIsInterfaceShown(true);
        };

        const hideInterface = () => {
            setIsInterfaceShown(false);
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        };

        const scheduleHide = () => {
            clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(hideInterface, time);
        };

        const makeInterfaceVisibleForAWhile = () => {
            showInterface();
            scheduleHide();
        };

        const throttledMakeVisible = throttle(makeInterfaceVisibleForAWhile, throttleTime);

        const isTargetInContainer = (target: HTMLElement | null): boolean => {
            if (!target || !container) return false;
            return container.contains(target) || container === target;
        };

        const handlePointerActivity = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return;

            const target = e.target as HTMLElement;

            if (isTargetInAlwaysVisible(target)) {
                showInterface();
                return;
            }

            if (isTargetInContainer(target)) {
                throttledMakeVisible();
            }
        };

        makeInterfaceVisibleForAWhile();

        container.addEventListener('pointermove', handlePointerActivity);
        container.addEventListener('pointerdown', handlePointerActivity);
        container.addEventListener('pointerover', handlePointerActivity);

        // глобальное отслеживание для alwaysVisible элементов (включая порталы)
        document.addEventListener('pointermove', handlePointerActivity, true);

        return () => {
            container.removeEventListener('pointermove', handlePointerActivity);
            container.removeEventListener('pointerdown', handlePointerActivity);
            container.removeEventListener('pointerover', handlePointerActivity);
            document.removeEventListener('pointermove', handlePointerActivity, true);
            clearTimeout(timerRef.current);
        };
    }, [ref, time, throttleTime, isTargetInAlwaysVisible]);

    return {
        isInterfaceShown,
        isInterfaceHidden: !isInterfaceShown,
        registerAlwaysVisibleElement,
    };
};
